<template>
  <v-card class="mx-auto mt-5" outlined tile>
    <v-toolbar flat>
      <v-row>
        <v-col cols="2">
          <v-select
            v-model="filter"
            :items="filterItems"
            hide-details
            single-line
            class="mx-2"
            prepend-icon="filter_list"
          />
        </v-col>
        <v-col cols="2">
          <v-select
            hide-details
            single-line
            v-model="searchBy"
            prepend-icon="manage_search"
            :items="searchByItems"
          />
        </v-col>
        <v-col cols="8">
          <registration-search-field
            name="search-registration"
            placeholder="Search"
            v-model="search"
            hide-details
            single-line
            prepend-icon="search"
            v-if="searchBy === 'registrationId'"
            v-bubble.error
          />
          <person-search-field
            name="search-person"
            placeholder="Search"
            v-model="search"
            hide-details
            single-line
            prepend-icon="search"
            v-if="searchBy === 'personId'"
            v-bubble.error
          />
          <v-text-field
            clearable
            hide-details
            single-line
            type="text"
            placeholder="Search"
            prepend-icon="search"
            v-model="search"
            v-if="searchBy === 'graderCode'"
          />
        </v-col>
      </v-row>
    </v-toolbar>

    <v-data-table
      dense
      must-sort
      :headers="headers"
      :options.sync="options"
      :footer-props="footerProps"
      :server-items-length="list.count"
      :items="list.items"
      :loading="$apollo.queries.list.loading"
      item-key="_id"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.graderCode }}</td>
          <td>{{ item.dataCaptureUnitPassword }}</td>
          <td>
            <router-link
              v-if="item.person"
              :to="{ name: 'AdminPerson', params: { id: item.personId } }"
            >
              {{ item.person.name }}
            </router-link>
          </td>
          <td>
            <router-link
              v-if="item.registration"
              :to="{ name: 'AdminRegistration', params: { id: item.registrationId } }"
            >
              {{ item.registration.memberNumber }}:&nbsp;{{ item.registration.memberName }}
            </router-link>
          </td>
          <td>{{ item.allocatedAt | formatDate }}</td>
          <td class="text-end">
            <v-btn
              small
              icon
              @click="edit(item)"
              :disabled="!!item.allocatedAt"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog"
      width="700"
    >
      <v-card>
        <v-card-title>
          Allocate Grader Code {{ form.graderCode }}
        </v-card-title>
        <v-card-text>
          <v-row class="pt-5">
            <v-col cols="12">
              <v-form>
                <person-search-field
                  v-model="form.personId"
                  v-bubble.error
                />
              </v-form>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="save"
          >
            Allocate
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import gql from 'graphql-tag'

import gqlSortMixin from '../mixins/gql-sort-mixin'
import PersonSearchField from './inputs/PersonSearchField.vue'
import RegistrationSearchField from './inputs/RegistrationSearchField.vue'

export default {
  name: 'GraderList',

  mixins: [
    gqlSortMixin
  ],

  components: {
    PersonSearchField,
    RegistrationSearchField
  },

  data: () => ({
    headers: [{
      text: 'Grader Code',
      value: 'graderCode'
    }, {
      text: 'DCU PIN',
      value: 'dataCaptureUnitPassword',
      sortable: false
    }, {
      text: 'Person',
      value: 'personId',
      sortable: false
    }, {
      text: 'Member Number',
      value: 'registrationId',
      sortable: false
    }, {
      text: 'Allocation date',
      value: 'allocatedAt'
    }, {
      text: 'Actions',
      sortable: false
    }],
    options: {
      sortDesc: [false],
      page: 1,
      itemsPerPage: 25,
      sortBy: ['graderCode']
    },
    footerProps: {
      'items-per-page-options': [10, 25, 50, 100, 200, 500]
    },
    list: {
      count: 0,
      items: []
    },
    dialog: false,
    form: {},
    search: '',
    filter: null,
    filterItems: [
      { text: 'All', value: null },
      { text: 'Allocated', value: { _operators: { allocatedAt: { nin: [null] } } } },
      { text: 'Unallocated', value: { allocatedAt: null } }
    ],
    searchBy: 'graderCode',
    searchByItems: [
      { text: 'Grader Code', value: 'graderCode' },
      { text: 'Member', value: 'registrationId' },
      { text: 'Person', value: 'personId' }
    ]
  }),

  apollo: {
    list: {
      query: gql`query GraderList($page: Int!, $perPage: Int!, $filter: FilterFindManyGraderInput!, $sort: SortFindManyGraderInput!) {
        list: graderManyPagination(page: $page, perPage: $perPage, filter: $filter, sort: $sort) {
          count
          items {
            _id
            graderCode
            dataCaptureUnitPassword
            allocatedAt
            personId
            person {
              _id
              name
            }
            registrationId
            registration {
              _id
              memberNumber
              memberName
            }
          }
        }
      }`,
      debounce: 250,
      variables () {
        const filter = {}
        if (this.search) filter[this.searchBy] = this.search
        if (this.filter) Object.assign(filter, this.filter)
        return {
          filter,
          sort: this.gqlSort(this.options),
          page: this.options.page,
          perPage: this.options.itemsPerPage
        }
      },
      error (e) {
        this.onError(e)
      }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    edit (item) {
      this.form = {
        _id: item._id,
        graderCode: item.graderCode,
        personId: item.personId
      }
      this.dialog = true
    },

    async save () {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdGraderInput!) {
            graderUpdateById(_id: $id, record: $record){
              record {
                _id
              }
            }
          }`,
          variables: {
            id: this.form._id,
            record: {
              personId: this.form.personId,
              allocatedAt: new Date()
            }
          }
        })
      } catch (e) {
        this.$emit('error', e)
        return
      }
      this.$emit('info', 'Grader saved.')
      this.$apollo.queries.list.refresh()
      this.dialog = false
    }
  },

  watch: {
    searchBy () {
      this.search = ''
    },
    search () {
      this.options.page = 1
    },
    filter () {
      this.options.page = 1
    }
  }
}
</script>
